import { useState, useCallback, useMemo } from 'react';

export interface BulkSelectionState {
  selectedIds: Set<string>;
  selectedCount: number;
  isAllSelected: boolean;
  isIndeterminate: boolean;
}

export interface BulkSelectionActions {
  toggleItem: (id: string) => void;
  toggleAll: (allIds: string[]) => void;
  clearSelection: () => void;
  selectItems: (ids: string[]) => void;
  deselectItems: (ids: string[]) => void;
  isSelected: (id: string) => boolean;
}

export function useBulkSelection(totalCount: number = 0): [BulkSelectionState, BulkSelectionActions] {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const state = useMemo((): BulkSelectionState => {
    const selectedCount = selectedIds.size;
    const isAllSelected = totalCount > 0 && selectedCount === totalCount;
    const isIndeterminate = selectedCount > 0 && selectedCount < totalCount;

    return {
      selectedIds,
      selectedCount,
      isAllSelected,
      isIndeterminate,
    };
  }, [selectedIds, totalCount]);

  const toggleItem = useCallback((id: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const toggleAll = useCallback((allIds: string[]) => {
    setSelectedIds(prev => {
      // If all current page items are selected, deselect all
      const allCurrentSelected = allIds.every(id => prev.has(id));
      
      if (allCurrentSelected) {
        // Remove all current page items from selection
        const newSet = new Set(prev);
        allIds.forEach(id => newSet.delete(id));
        return newSet;
      } else {
        // Add all current page items to selection
        const newSet = new Set(prev);
        allIds.forEach(id => newSet.add(id));
        return newSet;
      }
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const selectItems = useCallback((ids: string[]) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      ids.forEach(id => newSet.add(id));
      return newSet;
    });
  }, []);

  const deselectItems = useCallback((ids: string[]) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      ids.forEach(id => newSet.delete(id));
      return newSet;
    });
  }, []);

  const isSelected = useCallback((id: string) => {
    return selectedIds.has(id);
  }, [selectedIds]);

  const actions: BulkSelectionActions = {
    toggleItem,
    toggleAll,
    clearSelection,
    selectItems,
    deselectItems,
    isSelected,
  };

  return [state, actions];
}
